// thunks.ts
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { createAsyncThunk } from '@reduxjs/toolkit'
import { CancelToken } from 'axios'
import { buildAuthenticatedUser } from '../../common/authenticatedUser'
import {
  getSiteReadinessSites, getSiteReadinessDevices, getSiteReadinessDevicesDrawer, getMissingDevices
} from '../../data/siteReadiness'
import {
  SiteData, SiteDeviceData, DeviceDetailResponse, MissingDevicesData
} from '../../data/siteReadinessData'

/**
 * Retrieves the sites for the site readiness page
 */
export const siteReadiness = createAsyncThunk(
  'siteReadiness/siteReadiness',
  async (args: {
    userId: string
    sessionId: string
    dataCenter: string
    companyid: string | undefined
  }): Promise<SiteData[]> => {
    const {
      userId,
      sessionId,
      dataCenter,
      companyid
    } = args
    const authUser = buildAuthenticatedUser(sessionId, dataCenter, userId)
    if (companyid == null) {
      throw new Error('Site readiness requires a company id')
    }
    return getSiteReadinessSites(authUser, companyid)
  }
)

/**
 * Retrieves the devices for a site for site readiness.
 */
export const siteReadinessDevices = createAsyncThunk(
  'siteReadiness/siteReadinessDevices',
  async (args: {
    userId: string
    sessionId: string
    dataCenter: string
    siteId: string | undefined
    abortSignal: CancelToken
  }): Promise<SiteDeviceData[]> => {
    const {
      userId, sessionId, dataCenter, siteId, abortSignal
    } = args
    const authUser = buildAuthenticatedUser(sessionId, dataCenter, userId)
    if (siteId == null) {
      throw new Error('Site ID must be provided')
    }
    return getSiteReadinessDevices(abortSignal, authUser, siteId)
  }
)

export const getDeviceInfo = createAsyncThunk(
  'siteReadiness/getDeviceInfo',
  async (args: {
    userId: string
    sessionId: string
    dataCenter: string
    siteId: string | undefined
    deviceId: string | undefined
    abortSignal: CancelToken
  }): Promise<DeviceDetailResponse> => {
    const {
      userId, sessionId, dataCenter, siteId, deviceId, abortSignal
    } = args
    const authUser = buildAuthenticatedUser(sessionId, dataCenter, userId)
    if (siteId == null) {
      throw new Error('Site ID must be provided')
    }
    if (deviceId == null) {
      throw new Error('Device ID must be provided')
    }
    return getSiteReadinessDevicesDrawer(abortSignal, authUser, siteId, deviceId)
  }
)

export const missingDevices = createAsyncThunk(
  'siteReadiness/getMissingDevices',
  async (args: {
    userId: string
    sessionId: string
    dataCenter: string
    siteId: string | undefined
    abortSignal: CancelToken
  }): Promise<MissingDevicesData> => {
    const {
      userId, sessionId, dataCenter, siteId, abortSignal
    } = args
    const authUser = buildAuthenticatedUser(sessionId, dataCenter, userId)
    if (siteId == null) {
      throw new Error('Site ID must be provided')
    }
    return getMissingDevices(abortSignal, authUser, siteId)
  }
)
